import {
  MediaItem,
  Avatar,
  SubtitlesItem,
  TitleItem,
  Effects,
  AnimationConfig,
  CustomText,
  Shape,
  ParameteredText,
  MoveMethod,
  FadeMethod,
  TableTextStyle,
  TableDividerStyle,
} from "../../types";
import { Flex, Typography } from "antd";
import { Dropdown } from "@libertify/ui";
import { useState } from "react";
import { CommitSlider } from "./CommitSlider";
import { useTranslate } from "@refinedev/core";
import {
  ArrowFatLinesDownIcon,
  ArrowFatLinesUpIcon,
  SelectionForegroundIcon,
  SelectionBackgroundIcon,
  ArrowFatLinesLeftIcon,
  ArrowFatLinesRightIcon,
  CaretDownIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme";
import styled from "styled-components";
import {
  isEffectActive,
  getEffectDelay,
  getEffectDuration,
  getEffectMethod,
  getEffectGroup,
  isFadeEffect,
  normalizeAnimationConfig,
  type EffectGroup,
} from "../../helpers/animationHelpers";
import { DEFAULT_ANIMATION_DURATION_SECONDS } from "../../constants";

const StyledDropdownButton = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colorPrimary : theme.colorBorder};
  cursor: pointer;
  min-height: 36px;
  line-height: 1;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colorPrimary};
  }

  .ant-typography {
    line-height: 1;
  }
`;

const StyledChevron = styled(CaretDownIcon)<{ $open: boolean }>`
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  color: ${({ theme }) => theme.colorTextTertiary};
  flex-shrink: 0;
`;

const StyledSettingsPanel = styled(Flex)`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-top: none;
`;

const StyledClearButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colorTextTertiary};
  padding: 2px;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.colorTextBase};
    background: ${({ theme }) => theme.colorBgTextHover};
  }
`;

type PickedItemType =
  | SubtitlesItem
  | TitleItem
  | Avatar
  | MediaItem
  | CustomText
  | Shape
  | ParameteredText
  | TableTextStyle
  | TableDividerStyle;

export interface AnimationsProps {
  pickedItem?: PickedItemType;
  onChange: (value: Partial<PickedItemType>) => void;
}

const effectIcons: Record<Effects, React.ReactNode> = {
  [Effects.fadeIn]: <SelectionForegroundIcon size={18} weight="duotone" />,
  [Effects.fadeOut]: <SelectionBackgroundIcon size={18} weight="duotone" />,
  [Effects.enterFromTop]: <ArrowFatLinesDownIcon size={18} weight="duotone" />,
  [Effects.enterFromBottom]: <ArrowFatLinesUpIcon size={18} weight="duotone" />,
  [Effects.enterFromLeft]: (
    <ArrowFatLinesRightIcon size={18} weight="duotone" />
  ),
  [Effects.enterFromRight]: (
    <ArrowFatLinesLeftIcon size={18} weight="duotone" />
  ),
  [Effects.leaveToTop]: <ArrowFatLinesUpIcon size={18} weight="duotone" />,
  [Effects.leaveToBottom]: <ArrowFatLinesDownIcon size={18} weight="duotone" />,
  [Effects.leaveToLeft]: <ArrowFatLinesLeftIcon size={18} weight="duotone" />,
  [Effects.leaveToRight]: <ArrowFatLinesRightIcon size={18} weight="duotone" />,
};

const groupEffects: Record<EffectGroup, Effects[]> = {
  fadeIn: [Effects.fadeIn],
  fadeOut: [Effects.fadeOut],
  enterFrom: [
    Effects.enterFromTop,
    Effects.enterFromBottom,
    Effects.enterFromLeft,
    Effects.enterFromRight,
  ],
  leaveTo: [
    Effects.leaveToTop,
    Effects.leaveToBottom,
    Effects.leaveToLeft,
    Effects.leaveToRight,
  ],
};

const groupTranslationKeys: Record<EffectGroup, string> = {
  fadeIn: "components.templates.settings.effects.fadeIn",
  fadeOut: "components.templates.settings.effects.fadeOut",
  enterFrom: "components.templates.settings.effects.enter",
  leaveTo: "components.templates.settings.effects.leaveTo",
};

const GROUPS: EffectGroup[] = ["fadeIn", "fadeOut", "enterFrom", "leaveTo"];

const DURATION_MIN_S = 0.1;
const DURATION_MAX_S = 5;
const DELAY_MIN_S = 0;
const DELAY_MAX_S = 2;
const SLIDER_STEP_S = 0.1;

const DurationSlider = ({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (val: number) => void;
}) => (
  <CommitSlider
    value={value}
    onCommit={onCommit}
    min={DURATION_MIN_S}
    max={DURATION_MAX_S}
    step={SLIDER_STEP_S}
    tooltip={{ formatter: (val) => `${val}s` }}
  />
);

const DelaySlider = ({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (val: number) => void;
}) => (
  <CommitSlider
    value={value}
    onCommit={onCommit}
    min={DELAY_MIN_S}
    max={DELAY_MAX_S}
    step={SLIDER_STEP_S}
    tooltip={{ formatter: (val) => `${val}s` }}
  />
);

export const Animations = ({ pickedItem, onChange }: AnimationsProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [easingOpenGroup, setEasingOpenGroup] = useState<EffectGroup | null>(
    null
  );

  const getActiveEffectForGroup = (group: EffectGroup): Effects | null => {
    const effects = groupEffects[group];
    return (
      effects.find((e) => isEffectActive(pickedItem?.animations?.[e])) ?? null
    );
  };

  const handleSelectEffect = (effect: Effects) => {
    const group = getEffectGroup(effect);
    const currentAnimations = { ...pickedItem?.animations };

    for (const e of groupEffects[group]) {
      delete currentAnimations[e];
    }

    if (!isEffectActive(pickedItem?.animations?.[effect])) {
      currentAnimations[effect] = {
        enabled: true,
        duration: DEFAULT_ANIMATION_DURATION_SECONDS,
        method: MoveMethod.EaseInOut,
      };
    }

    onChange({ ...pickedItem, animations: currentAnimations });
  };

  const handleClearGroup = (group: EffectGroup) => {
    const currentAnimations = { ...pickedItem?.animations };
    for (const e of groupEffects[group]) {
      delete currentAnimations[e];
    }
    onChange({ ...pickedItem, animations: currentAnimations });
  };

  const handleUpdateEffectConfig = (
    effect: Effects,
    updates: Partial<AnimationConfig>
  ) => {
    const currentValue = pickedItem?.animations?.[effect];
    const config = normalizeAnimationConfig(currentValue) ?? {
      enabled: true,
      duration: DEFAULT_ANIMATION_DURATION_SECONDS,
      method: MoveMethod.EaseInOut,
    };
    onChange({
      ...pickedItem,
      animations: {
        ...pickedItem?.animations,
        [effect]: { ...config, ...updates },
      },
    });
  };

  const getMethodOptions = (effect: Effects) => {
    if (isFadeEffect(effect)) {
      return Object.values(FadeMethod).map((m) => ({
        value: m,
        label: t(`components.templates.settings.effects.methods.${m}`),
      }));
    }
    return Object.values(MoveMethod).map((m) => ({
      value: m,
      label: t(`components.templates.settings.effects.methods.${m}`),
    }));
  };

  return (
    <Flex vertical gap={12}>
      {GROUPS.map((group) => {
        const activeEffect = getActiveEffectForGroup(group);
        const effects = groupEffects[group];
        const isSingleItem = effects.length === 1;
        const groupLabel = t(groupTranslationKeys[group]);

        const menuItems = effects.map((effect) => {
          const picked = isEffectActive(pickedItem?.animations?.[effect]);
          return {
            key: effect,
            label: (
              <Flex
                gap={8}
                align="center"
                style={{
                  color: picked ? theme.colorPrimary : theme.colorTextSecondary,
                }}
              >
                {effectIcons[effect]}
                {t(`components.templates.settings.effects.${effect}`)}
              </Flex>
            ),
            onClick: () => handleSelectEffect(effect),
          };
        });

        return (
          <Flex vertical key={group}>
            <Dropdown
              items={menuItems}
              trigger={["click"]}
              getPopupContainer={(triggerNode) =>
                triggerNode.parentElement ?? triggerNode
              }
            >
              <StyledDropdownButton $active={!!activeEffect}>
                {activeEffect ? (
                  <Flex gap={8} align="center">
                    {effectIcons[activeEffect]}
                    <Typography.Text
                      style={{
                        fontSize: 13,
                        color: theme.colorTextBase,
                      }}
                    >
                      {isSingleItem
                        ? groupLabel
                        : `${groupLabel} ${t(`components.templates.settings.effects.${activeEffect}`)}`}
                    </Typography.Text>
                  </Flex>
                ) : (
                  <Typography.Text
                    style={{
                      fontSize: 13,
                      color: theme.colorTextSecondary,
                    }}
                  >
                    {groupLabel}
                  </Typography.Text>
                )}
                {activeEffect && (
                  <StyledClearButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearGroup(group);
                    }}
                  >
                    <XIcon size={14} />
                  </StyledClearButton>
                )}
              </StyledDropdownButton>
            </Dropdown>
            {activeEffect && (
              <StyledSettingsPanel vertical gap={6}>
                <Flex vertical>
                  <Typography.Text
                    style={{ fontSize: 11, color: theme.colorTextSecondary }}
                  >
                    {t("components.templates.settings.effects.duration")}
                  </Typography.Text>
                  <DurationSlider
                    value={getEffectDuration(
                      pickedItem?.animations?.[activeEffect]
                    )}
                    onCommit={(val) =>
                      handleUpdateEffectConfig(activeEffect, { duration: val })
                    }
                  />
                </Flex>
                {(group === "fadeIn" || group === "enterFrom") && (
                  <Flex vertical>
                    <Typography.Text
                      style={{ fontSize: 11, color: theme.colorTextSecondary }}
                    >
                      {t("components.templates.settings.effects.delay")}
                    </Typography.Text>
                    <DelaySlider
                      value={getEffectDelay(
                        pickedItem?.animations?.[activeEffect]
                      )}
                      onCommit={(val) =>
                        handleUpdateEffectConfig(activeEffect, { delay: val })
                      }
                    />
                  </Flex>
                )}
                <Dropdown
                  items={getMethodOptions(activeEffect).map((opt) => ({
                    key: opt.value,
                    label: (
                      <Typography.Text
                        style={{
                          color:
                            getEffectMethod(
                              pickedItem?.animations?.[activeEffect]
                            ) === opt.value
                              ? theme.colorPrimary
                              : theme.colorTextSecondary,
                          fontSize: 13,
                        }}
                      >
                        {opt.label}
                      </Typography.Text>
                    ),
                    onClick: () =>
                      handleUpdateEffectConfig(activeEffect, {
                        method: opt.value,
                      }),
                  }))}
                  trigger={["click"]}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentElement ?? triggerNode
                  }
                  onOpenChange={(open) =>
                    setEasingOpenGroup(open ? group : null)
                  }
                >
                  <StyledDropdownButton>
                    <Typography.Text
                      style={{ fontSize: 12, color: theme.colorTextSecondary }}
                    >
                      {t(
                        `components.templates.settings.effects.methods.${getEffectMethod(pickedItem?.animations?.[activeEffect])}`
                      )}
                    </Typography.Text>
                    <StyledChevron
                      size={14}
                      $open={easingOpenGroup === group}
                    />
                  </StyledDropdownButton>
                </Dropdown>
              </StyledSettingsPanel>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
